<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import AuthLayout       from './AuthLayout.vue'
import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'
import FieldLeaf        from '@/design/primitives/FieldLeaf.vue'
import ButtonSeed       from '@/design/primitives/ButtonSeed.vue'
import { api } from '@/api/client.js'
import { EP }  from '@/api/endpoints.js'

const route = useRoute()

const token    = computed(() => route.query.token || '')
const password = ref('')
const confirm  = ref('')
const loading  = ref(false)
const error    = ref('')
const success  = ref(false)

async function handleSubmit() {
  error.value = ''

  if (!password.value) {
    error.value = 'PLEASE ENTER A NEW PASSWORD.'
    return
  }
  if (password.value.length < 6) {
    error.value = 'PASSWORD MUST BE AT LEAST 6 CHARACTERS.'
    return
  }
  if (password.value !== confirm.value) {
    error.value = 'PASSWORDS DO NOT MATCH.'
    return
  }

  loading.value = true
  try {
    await api.post(EP.authReset, { token: token.value, password: password.value })
    success.value = true
  } catch (err) {
    error.value = (err.message || 'COULD NOT RESET — LINK MAY HAVE EXPIRED.').toUpperCase()
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <AuthLayout kicker="EVOLUXU · RESET" stamp="Forge a new key.">

    <!-- No token in URL -->
    <template v-if="!token">
      <div class="seal seal--status u-rise-in">
        <p class="seal__label u-display u-allcaps">INVALID OR EXPIRED LINK</p>
        <p class="seal__body">This reset link is no longer valid.</p>
        <RouterLink to="/forgot-password" class="plate__link u-display u-allcaps">
          REQUEST A NEW LINK
        </RouterLink>
      </div>
    </template>

    <!-- Success state -->
    <template v-else-if="success">
      <div class="seal seal--status u-rise-in">
        <p class="seal__label u-display u-allcaps u-gold">PASSWORD UPDATED</p>
        <p class="seal__body">Your password has been reset successfully.</p>
        <RouterLink to="/register?tab=signin" class="plate__link u-display u-allcaps">
          SIGN IN
        </RouterLink>
      </div>
    </template>

    <!-- Form state -->
    <template v-else>
      <HeadlineFraunces level="h2" class="plate__heading">
        New <em>password</em>.
      </HeadlineFraunces>

      <form class="seal" @submit.prevent="handleSubmit" novalidate>
        <FieldLeaf
          v-model="password"
          label="NEW PASSWORD"
          type="password"
          placeholder="Min. 6 characters"
          required
        />
        <FieldLeaf
          v-model="confirm"
          label="CONFIRM PASSWORD"
          type="password"
          placeholder="Repeat new password"
          required
        />

        <p v-if="error" class="seal__error u-display u-allcaps" role="alert">{{ error }}</p>

        <ButtonSeed type="submit" size="lg" :disabled="loading" class="seal__submit">
          {{ loading ? 'SAVING…' : 'SET NEW PASSWORD' }}
        </ButtonSeed>
      </form>
    </template>

  </AuthLayout>
</template>

<style scoped>
.plate__heading {
  margin-bottom: var(--sp-8);
}

/* ── Status panels (no-token / success) ── */
.seal--status {
  text-align: center;
  padding: var(--sp-8) 0;
}

.seal__label {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-21);
  letter-spacing: 0.08em;
  color: var(--ink-soft);
}

.seal__body {
  margin: 0 0 var(--sp-6);
  font-size: var(--fs-16);
  color: var(--ink-soft);
  line-height: var(--lh-body);
}

/* ── Form seal ── */
.seal {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.seal__submit { width: 100%; }

.seal__error {
  font-size: var(--fs-12);
  color: var(--ember);
  margin: 0;
  letter-spacing: 0.08em;
}

/* ── Action link ── */
.plate__link {
  display: inline-block;
  font-size: var(--fs-12);
  letter-spacing: 0.12em;
  color: var(--gold);
  opacity: 0.8;
  transition: opacity var(--t-fast) var(--ease-road);
}

.plate__link:hover { opacity: 1; }
</style>
